<template>
  <lf-modal :title="modalTitle" title-border :close="() => emit('close')">
    <template #content>
      <div class="w-full">
        <div class="mt-3 p-6">
          <lf-input
            :placeholder="`${$t('INTEGRATIONS.BRAND')} ${$t('COMMON.NAME')}`"
            :value="brandName?.name || ''"
            :maxlength="255"
            name="name"
            data-cy="brand-name-input"
          />
        </div>
        <hr />
        <modal-buttons
          class="justify-end py-6"
          :on-cancel="() => emit('close')"
          :on-save="handleSave"
          :save-button-text="brandName ? $t('COMMON.SAVE') : $t('COMMON.ADD')"
          :disabled="isSubmitting"
        />
      </div>
    </template>
  </lf-modal>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { toFormValidator } from "@vee-validate/zod";
import { useForm } from "vee-validate";
import { z } from "zod";

import { submitWithErrorHandling } from "@/helpers/vee-validate";
import {
  useCreateBrandNameMutation,
  useUpdateBrandNameMutation,
} from "@/hooks/queries/useBrandNames";

import LfModal from "@/components/ui/Modal.vue";
import ModalButtons from "@/components/ui/ModalButtons.vue";

import type { BrandName } from "@/schemas/brandName";

interface Props {
  brandName?: BrandName | null;
}

const { brandName = null } = defineProps<Props>();

const emit = defineEmits<{
  close: [];
  created: [brandName: BrandName];
  updated: [brandName: BrandName];
}>();

const { t } = useI18n();
const { mutateAsync: createBrandName } = useCreateBrandNameMutation();
const { mutateAsync: updateBrandName } = useUpdateBrandNameMutation();

const modeText = computed(() =>
  brandName ? t("COMMON.EDIT") : t("COMMON.ADD")
);
const modalTitle = computed(
  () => `${modeText.value} ${t("INTEGRATIONS.BRAND")} ${t("COMMON.NAME")}`
);

const validationSchema = z.object({
  name: z.string().min(1, t("COMMON.REQUIRED")).max(255),
});

const { handleSubmit, isSubmitting } = useForm({
  initialValues: { name: brandName?.name || "" },
  validationSchema: toFormValidator(validationSchema),
});

const handleSave = handleSubmit(async (values, actions) => {
  await submitWithErrorHandling(actions, async () => {
    if (brandName) {
      const response = await updateBrandName({
        brandNameId: brandName.id,
        data: values,
      });
      emit("updated", response.data);
    } else {
      const response = await createBrandName(values);
      emit("created", response.data);
    }
    emit("close");
  });
});
</script>
